import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styled from '@emotion/styled/macro';
import { Formik, Form } from 'formik';
import { Heading, Box, SimpleGrid, Flex, HStack, Radio, Text, Divider, useDisclosure } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { isNumberCellEmpty } from 'services/items';
import { set, isAfter, isEqual, isBefore } from 'date-fns';
import { createSubBatteryFromWizard } from 'services/batteries';
import { removeNullish } from 'services/utils';
import { mapGeneralValidationMessages } from '../../../autoform/utils/autoformUtils';

import InputFormikField from '../../../forms/InputFormikField';
import RadioGroupFormikField from '../../../forms/RadioGroupFormikField';
import DateFormikField from '../../../forms/DateFormikField';
import NumberInputFormikField from '../../../forms/NumberInputFormikField';
import NumberInputField from '../../../forms/NumberInputField';

import GeneralFormValidationsAlert from '../../../forms/GeneralFormValidationsAlert';
import AutomaticFormikDirtyCheck from '../../../forms/AutomaticFormikDirtyCheck';

import useItemCreationWizard from 'hooks/useItemCreationWizard';
import NavigationButtons from '../NavigationButtons';

const BatteryStorageCustomSubItemStep = () => {
    const intl = useIntl();
    const errorsDisclosure = useDisclosure({ defaultIsOpen: true });
    const warningsDisclosure = useDisclosure({ defaultIsOpen: true });

    const [isFormDirty, setIsFormDirty] = useState(false);
    const [generalErrors, setGeneralErrors] = useState([]);
    const [generalWarnings, setGeneralWarnings] = useState([]);
    const [globalErrors, setGlobalErrors] = useState([]);
    const [globalWarnings, setGlobalWarnings] = useState([]);
    const [singleDatePeriodReadOnly, setSingleDatePeriodReadOnly] = useState(false);
    const [annualDegradation, setAnnualDegradation] = useState(0);

    const allErrors = useMemo(() => [...globalErrors, ...generalErrors], [generalErrors, globalErrors]);
    const allWarnings = useMemo(() => [...globalWarnings, ...generalWarnings], [generalWarnings, globalWarnings]);

    const { newItemId, moveToSubItemDecisionStep, moveToItemPageStep, onFormHasUnsavedChanges } =
        useItemCreationWizard();

    const formLabels = {
        description: intl.formatMessage({ id: 'batteries_sub_battery_name_label' }),
        batteryType: intl.formatMessage({ id: 'batteries_battery_type' }),
        startDate: intl.formatMessage({ id: 'common_grid_start_date' }),
        endDate: intl.formatMessage({ id: 'common_grid_end_date' }),
        mwPower: intl.formatMessage({ id: 'batteries_battery_wizard_mw_power' }),
        hrDuration: intl.formatMessage({ id: 'batteries_battery_wizard_hr_duration' }),
        chargeEfficiency: intl.formatMessage({ id: 'batteries_battery_wizard_charge_efficiency' }),
        dischargeEfficiency: intl.formatMessage({ id: 'batteries_battery_wizard_discharge_efficiency' }),
        vom: intl.formatMessage({ id: 'batteries_battery_wizard_vom_discharging' }),
        fixedCost: intl.formatMessage({ id: 'batteries_battery_wizard_fixed_costs' }),
        annualCycleLimit: intl.formatMessage({ id: 'batteries_battery_wizard_annual_cycle_limit' }),
        costlessAdder: intl.formatMessage({ id: 'batteries_battery_wizard_costless_adder' }),
        minAverageSOC: intl.formatMessage({ id: 'batteries_battery_wizard_min_average_soc' }),
        maxAverageSOC: intl.formatMessage({ id: 'batteries_battery_wizard_max_average_soc' }),
        annualDegradation: intl.formatMessage({ id: 'batteries_battery_wizard_annual_degradation' }),
        emptyPlaceholder: '',
    };

    const initialFormValues = {
        description: '',
        batteryTypeId: '',
        itemId: parseInt(newItemId), // the parent battery item id
        startDate: '',
        endDate: '',
        mwPower: '',
        hrDuration: '',
        chargeEfficiency: '',
        dischargeEfficiency: '',
        vom: '',
        fixedCost: '',
        annualCycleLimit: '',
        costlessAdder: '',
        minAverageSOC: 0,
        maxAverageSOC: 100,
        singlePeriodMode: '0',
        annualDegradation: '',
    };

    useEffect(() => {
        onFormHasUnsavedChanges(isFormDirty);
    }, [isFormDirty, onFormHasUnsavedChanges]);

    const validateDescription = (value) => {
        const trimmed = value.trim();

        if (!trimmed) {
            return intl.formatMessage({ id: 'common_forms_validation_required' }, { label: formLabels.description });
        }

        if (trimmed.length > 50) {
            return intl.formatMessage(
                { id: 'common_forms_validation_length' },
                { label: formLabels.description, lengthRule: '50 characters or less' }
            );
        }
    };

    const validateRequiredField = (value, label) => {
        if (value === '') {
            return intl.formatMessage({ id: 'common_forms_validation_required' }, { label });
        }
    };

    const validateSiblingDate = ({ value, siblingValue, type }) => {
        // when you delete the contents of the datepicker the value is null or empty string
        const isInvalidValue = value === null || value === '';
        const isInvalidSiblingValue = siblingValue === null || siblingValue === '';

        if (isInvalidValue || (isInvalidValue && isInvalidSiblingValue)) {
            // show an error if the value is invalid or both the value and the sibling value are invalid
            return intl.formatMessage(
                { id: 'common_forms_validation_required' },
                { label: type === 'start' ? formLabels.startDate : formLabels.endDate }
            );
        }

        // skip validation on invalid sibling
        if (isInvalidSiblingValue) {
            return null;
        }

        const dateA = set(value, { seconds: 0, milliseconds: 0 });
        const dateB = set(siblingValue, { seconds: 0, milliseconds: 0 });

        if (type === 'start' && (isAfter(dateA, dateB) || isEqual(dateA, dateB))) {
            return intl.formatMessage({ id: 'batteries_common_modals_date_smaller' });
        } else if (type === 'end' && (isBefore(dateA, dateB) || isEqual(dateA, dateB))) {
            return intl.formatMessage({ id: 'batteries_common_modals_date_bigger' });
        }

        return null;
    };

    const validateForm = (values) => {
        const description = validateDescription(values.description);

        const startDate = validateSiblingDate({
            value: values.startDate,
            siblingValue: values.endDate,
            type: 'start',
        });

        const endDate = validateSiblingDate({
            value: values.endDate,
            siblingValue: values.startDate,
            type: 'end',
        });

        const mwPower = validateRequiredField(values.mwPower, formLabels.mwPower);
        const hrDuration = validateRequiredField(values.hrDuration, formLabels.hrDuration);
        const chargeEfficiency = validateRequiredField(values.chargeEfficiency, formLabels.chargeEfficiency);
        const dischargeEfficiency = validateRequiredField(values.dischargeEfficiency, formLabels.dischargeEfficiency);

        return removeNullish({
            description,
            startDate,
            endDate,
            mwPower,
            hrDuration,
            chargeEfficiency,
            dischargeEfficiency,
        });
    };

    const formatFieldValues = useCallback(
        (values) => {
            return {
                itemId: values.itemId,
                description: values.description,
                startDate: values.startDate || null,
                endDate: values.endDate || null,
                mwPower: isNumberCellEmpty(values.mwPower),
                hrDuration: isNumberCellEmpty(values.hrDuration),
                chargeEfficiency: isNumberCellEmpty(values.chargeEfficiency),
                dischargeEfficiency: isNumberCellEmpty(values.dischargeEfficiency),
                vom: isNumberCellEmpty(values.vom),
                fixedCost: isNumberCellEmpty(values.fixedCost),
                annualCycleLimit: isNumberCellEmpty(values.annualCycleLimit),
                costlessAdder: isNumberCellEmpty(values.costlessAdder),
                minAverageSOC: isNumberCellEmpty(values.minAverageSOC),
                maxAverageSOC: isNumberCellEmpty(values.maxAverageSOC),
                singlePeriodMode: values.singlePeriodMode,
                annualDegradation: isNumberCellEmpty(annualDegradation),
            };
        },
        [annualDegradation]
    );

    const resetServerValidation = useCallback(() => {
        setGeneralErrors([]);
        setGeneralWarnings([]);
        setGlobalErrors([]);
        setGlobalWarnings([]);
    }, []);

    const onFormSubmit = async (values, { setErrors, setStatus }) => {
        try {
            onFormHasUnsavedChanges(false);

            const payload = formatFieldValues(values);
            await createSubBatteryFromWizard(payload);

            moveToItemPageStep();
        } catch (err) {
            // reset the previous general errors and warnings
            resetServerValidation();

            // make sure to re-open the disclosures again,
            // because the user might have closed them and is submitting the form again
            errorsDisclosure.onOpen();
            warningsDisclosure.onOpen();

            if (err.response.status !== 500) {
                if (err.response.data.validationResult.issues) {
                    // when creating a single record we expect at most only 1 issue
                    const issue = err.response.data.validationResult.issues[0];

                    // handle general errors and warnings
                    const { warnings, errors } = mapGeneralValidationMessages(issue.generalValidationMessages);
                    setGeneralWarnings(warnings);
                    setGeneralErrors(errors);

                    // handle specific field errors
                    let fieldErrors = {};
                    let fieldStatus = {};

                    for (const field of issue.fields) {
                        const errors = field.validationMessages
                            .filter(({ severity }) => severity === 'Error')
                            .map(({ message }) => message);

                        const warnings = field.validationMessages
                            .filter(({ severity }) => severity === 'Warning')
                            .map(({ message }) => message);
                        fieldStatus[field.name] = { errors, warnings };
                    }

                    setErrors(fieldErrors);
                    setStatus(fieldStatus);
                }

                if (err.response.data.validationResult.globalIssues) {
                    // handle global errors and warnings
                    const { warnings, errors } = mapGeneralValidationMessages(
                        err.response.data.validationResult.globalIssues
                    );
                    setGlobalWarnings(warnings);
                    setGlobalErrors(errors);
                }
            }
        }
    };

    const handlePreviousStep = () => {
        moveToSubItemDecisionStep();
    };

    const degradationCheckDateMode = useCallback((degradationValue, setFieldValue) => {
        setAnnualDegradation(degradationValue);

        if (degradationValue === '' || degradationValue === null || degradationValue <= 0) {
            //no degradation value set, so allow single date period
            setSingleDatePeriodReadOnly(false);
        } else if (degradationValue !== '' && degradationValue !== null && degradationValue > 0) {
            //degradation value set, so disable single date period
            setSingleDatePeriodReadOnly(true);
            setFieldValue('singlePeriodMode', '0'); // use Formik function to set the date period mode to monthly
        }
    }, []);

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={initialFormValues}
                onSubmit={onFormSubmit}
                validate={validateForm}
            >
                {({ values, isSubmitting, errors, touched, setErrors, setStatus, setTouched, setFieldValue }) => {
                    return (
                        <Form noValidate>
                            <AutomaticFormikDirtyCheck isFormDirty={isFormDirty} onFormDirtyChange={setIsFormDirty} />
                            <CenteredWrapper>
                                <Heading pt={7} pb={5}>
                                    <FormattedMessage id="item_creation_wizard_custom_battery_heading" />
                                </Heading>
                                {allErrors.length > 0 && (
                                    <GeneralFormValidationsAlert
                                        isOpen={errorsDisclosure.isOpen}
                                        onClose={errorsDisclosure.onClose}
                                        messages={allErrors}
                                    />
                                )}

                                {allWarnings.length > 0 && (
                                    <GeneralFormValidationsAlert
                                        status="warning"
                                        title={<FormattedMessage id="common_warning" />}
                                        isOpen={warningsDisclosure.isOpen}
                                        onClose={warningsDisclosure.onClose}
                                        messages={allWarnings}
                                    />
                                )}

                                <SimpleGrid columns={1} spacingY={3} pb={3}>
                                    <InputFormikField
                                        name="description"
                                        type="text"
                                        label={formLabels.description}
                                        isRequired
                                    />
                                </SimpleGrid>
                                <Flex>
                                    <FormattedMessage id="batteries_battery_wizard_date_mode" />
                                    <Text ml={4} color="grey">
                                        <FormattedMessage id="batteries_battery_wizard_date_mode_note" />
                                    </Text>
                                </Flex>

                                <RadioGroupFormikField mx={0} mb={3} id="dateMode" name="singlePeriodMode">
                                    <HStack spacing={8}>
                                        <StyledRadio value={'0'}>{'Monthly'}</StyledRadio>
                                        {!singleDatePeriodReadOnly && (
                                            <StyledRadio value={'1'} disabled={singleDatePeriodReadOnly}>
                                                {'Single date period'}
                                            </StyledRadio>
                                        )}
                                    </HStack>
                                </RadioGroupFormikField>

                                <SimpleGrid columns={2} gap={6} py={3}>
                                    <DateFormikField
                                        id="startDate"
                                        name="startDate"
                                        label={formLabels.startDate}
                                        isRequired
                                        showMonthYearPicker
                                        showTimeInput={false}
                                        info={<FormattedMessage id="batteries_battery_wizard_start_date_info" />}
                                    />

                                    <DateFormikField
                                        id="endDate"
                                        name="endDate"
                                        label={formLabels.endDate}
                                        isRequired
                                        showMonthYearPicker
                                        showTimeInput={false}
                                        info={<FormattedMessage id="batteries_battery_wizard_end_date_info" />}
                                    />

                                    <NumberInputFormikField
                                        id="mwPower"
                                        name="mwPower"
                                        label={formLabels.mwPower}
                                        isRequired
                                    />

                                    <NumberInputFormikField
                                        id="hrDuration"
                                        name="hrDuration"
                                        label={formLabels.hrDuration}
                                        isRequired
                                    />

                                    <NumberInputFormikField
                                        id="chargeEfficiency"
                                        name="chargeEfficiency"
                                        label={formLabels.chargeEfficiency}
                                        isRequired
                                        clampValueOnBlur={true}
                                        keepWithinRange={true}
                                        min={0}
                                        max={100}
                                    />

                                    <NumberInputFormikField
                                        id="dischargeEfficiency"
                                        name="dischargeEfficiency"
                                        label={formLabels.dischargeEfficiency}
                                        isRequired
                                        clampValueOnBlur={true}
                                        keepWithinRange={true}
                                        min={0}
                                        max={100}
                                    />
                                </SimpleGrid>

                                <Divider my={6} />

                                <Heading as="h4" variant="h4" textTransform="capitalize">
                                    <FormattedMessage
                                        id="batteries_battery_wizard_optional_title"
                                        textTransform="capitalize"
                                    />
                                </Heading>
                                <Box pb={3}>
                                    <FormattedMessage
                                        id="batteries_battery_wizard_optional_explanation"
                                        textTransform="capitalize"
                                    />
                                </Box>

                                <SimpleGrid columns={2} gap={6} py={3}>
                                    <NumberInputFormikField
                                        id="vom"
                                        name="vom"
                                        label={formLabels.vom}
                                        isRequired={false}
                                    />

                                    <NumberInputFormikField
                                        id="fixedCost"
                                        name="fixedCost"
                                        label={formLabels.fixedCost}
                                        isRequired={false}
                                    />

                                    <NumberInputFormikField
                                        id="annualCycleLimit"
                                        name="annualCycleLimit"
                                        label={formLabels.annualCycleLimit}
                                        isRequired={false}
                                    />

                                    <NumberInputFormikField
                                        id="costlessAdder"
                                        name="costlessAdder"
                                        label={formLabels.costlessAdder}
                                        isRequired={false}
                                    />

                                    <NumberInputFormikField
                                        id="minAverageSOC"
                                        name="minAverageSOC"
                                        label={formLabels.minAverageSOC}
                                        isRequired={false}
                                        clampValueOnBlur={true}
                                        keepWithinRange={true}
                                        min={0}
                                        max={100}
                                    />

                                    <NumberInputFormikField
                                        id="maxAverageSOC"
                                        name="maxAverageSOC"
                                        label={formLabels.maxAverageSOC}
                                        isRequired={false}
                                        clampValueOnBlur={true}
                                        keepWithinRange={true}
                                        min={0}
                                        max={100}
                                    />
                                    <NumberInputField
                                        id="annualDegradation"
                                        name="annualDegradation"
                                        label={formLabels.annualDegradation}
                                        placeholder="-"
                                        min={0}
                                        max={100}
                                        precision={0}
                                        value={annualDegradation}
                                        onChange={(value) => degradationCheckDateMode(value, setFieldValue)}
                                        info={
                                            <FormattedMessage id="batteries_battery_wizard_annual_degradation_info" />
                                        }
                                    />
                                </SimpleGrid>
                            </CenteredWrapper>
                            <NavigationButtons
                                isPrevStepDisabled={isSubmitting}
                                isConfirmSubmitting={isSubmitting}
                                isConfirmDisabled={isSubmitting}
                                handlePreviousStep={handlePreviousStep}
                            />
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};

const CenteredWrapper = styled(Box)`
    width: 100%;
    max-width: 1314px;
    margin: 0 auto;
    padding: 0 16px;
`;

const StyledRadio = styled(Radio)`
    margin-right: 2px;
    border-color: var(--chakra-colors-green-500);

    &:hover {
        border-color: var(--chakra-colors-blue-400);
    }
`;

export default BatteryStorageCustomSubItemStep;
